import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';

import text from './nuiText';
import { startOAuth, enabledProviders } from './nuiOAuthLib';

/** Логотип Google в фирменных цветах - требование его же brand guidelines для
 * кнопки входа. */
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.8591-3.0477.8591-2.344 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z"/>
    <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z"/>
    <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"/>
  </svg>
);

/** Логотип LinkedIn в фирменном цвете (#0A66C2). */
const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#0A66C2" d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
  </svg>
);

/** Логотип Facebook в фирменном цвете (#1877F2). */
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/>
  </svg>
);

/** Логотип Яндекс ID: белая «Я» в фирменном красном круге (#FC3F1D). */
const YandexIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#FC3F1D"/>
    <path fill="#fff" d="M13.1 6.4h-1.05c-1.5 0-2.3.78-2.3 1.93 0 1.3.55 1.9 1.68 2.68l.94.64-2.7 4.06H7.66l2.43-3.63C8.7 11.08 7.92 10.12 7.92 8.46c0-2.08 1.44-3.5 4.12-3.5h3.03v12.75h-1.97V6.4z"/>
  </svg>
);

/** Логотип VK в фирменном синем (#0077FF). */
const VkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#0077FF" d="M13.16 18.24c-6.63 0-10.83-4.62-11-12.3h3.34c.11 5.64 2.68 8.05 4.63 8.53V5.94h3.2v4.8c1.88-.2 3.85-2.4 4.51-4.8h3.13c-.5 2.95-2.65 5.15-4.17 6.06 1.52.74 3.96 2.66 4.9 6.24h-3.44c-.73-2.3-2.53-4.08-4.93-4.32v4.32h-.17z"/>
  </svg>
);

const providerView = {
  google: {
    icon: <GoogleIcon />,
    label: () => text({"ru": "Войти через Google",
                       "ua": "Увійти через Google",
                       "en": "Sign in with Google",
                       "es": "Iniciar sesión con Google"})
  },
  linkedin: {
    icon: <LinkedInIcon />,
    label: () => text({"ru": "Войти через LinkedIn",
                       "ua": "Увійти через LinkedIn",
                       "en": "Sign in with LinkedIn",
                       "es": "Iniciar sesión con LinkedIn"})
  },
  facebook: {
    icon: <FacebookIcon />,
    label: () => text({"ru": "Войти через Facebook",
                       "ua": "Увійти через Facebook",
                       "en": "Sign in with Facebook",
                       "es": "Iniciar sesión con Facebook"})
  },
  vkid: {
    icon: <VkIcon />,
    label: () => text({"ru": "Войти через VK ID",
                       "ua": "Увійти через VK ID",
                       "en": "Sign in with VK ID",
                       "es": "Iniciar sesión con VK ID"})
  },
  yandex: {
    icon: <YandexIcon />,
    label: () => text({"ru": "Войти через Яндекс ID",
                       "ua": "Увійти через Яндекс ID",
                       "en": "Sign in with Yandex ID",
                       "es": "Iniciar sesión con Yandex ID"})
  }
};

/**
 * Кнопки входа через соцсети.
 *
 * Состав берётся из страницы: сервер печатает туда провайдеров, реально
 * включённых на ЭТОМ деплое. Поэтому кнопка не может появиться там, где роут
 * всё равно ответит 404 - в частности на finparser, где иностранные
 * провайдеры запрещены.
 *
 * props: lang, residenceCountry, onStart, onSuccess, onError
 */
export default function NuiOAuthButtons(props) {
  const providers = enabledProviders().filter(p => providerView[p] != undefined);
  if (providers.length === 0) {
    return null;
  }
  const click = (provider) => () => {
    if (props.onStart != undefined) {
      props.onStart();
    }
    startOAuth({
      provider: provider,
      lang: props.lang,
      residenceCountry: props.residenceCountry,
      onSuccess: props.onSuccess,
      onError: props.onError
    });
  };
  return (
    <Box sx={{pl: 4, pr: 4, pt: 1}}>
      <Divider sx={{pb: 1}}>
        {text({"ru": "или", "ua": "або", "en": "or", "es": "o"})}
      </Divider>
      {providers.map(provider => (
        <Button
          key={provider}
          fullWidth
          variant="outlined"
          color="inherit"
          startIcon={providerView[provider].icon}
          onClick={click(provider)}
          sx={{mt: 1, textTransform: "none"}}>
          {providerView[provider].label()}
        </Button>
      ))}
    </Box>
  );
}
